import { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuthStore } from '@/stores/authStore'
import LoginPage from '@/pages/auth/LoginPage'
import AppLayout from '@/components/layout/AppLayout'
import HomePage from '@/pages/HomePage'
import ScannerConnectPage from '@/pages/scanner/ScannerConnectPage'
import ScannerInfoPage from '@/pages/scanner/ScannerInfoPage'
import RawScanPage from '@/pages/scanner/RawScanPage'
import RecentScansPage from '@/pages/scanner/RecentScansPage'
import ProfilePage from '@/pages/ProfilePage'
import DocumentListPage from '@/pages/wizard/DocumentListPage'
import WizardCapturePage from '@/pages/wizard/WizardCapturePage'
import WizardScanPage from '@/pages/wizard/WizardScanPage'
import WizardConfirmPage from '@/pages/wizard/WizardConfirmPage'
import WizardSuccessPage from '@/pages/wizard/WizardSuccessPage'
import TransferCustomerPage from '@/pages/transfer/TransferCustomerPage'
import TransferToCustomerPage from '@/pages/transfer/TransferToCustomerPage'
import TransferDocListPage from '@/pages/transfer/TransferDocListPage'
import TransferItemPickPage from '@/pages/transfer/TransferItemPickPage'
import EngineHubPage from '@/pages/engine/EngineHubPage'
import EngineBirthPage from '@/pages/engine/EngineBirthPage'
import EngineTagPage from '@/pages/engine/EngineTagPage'
import EngineLookupPage from '@/pages/engine/EngineLookupPage'
import { RfidEngineAdminRoute } from '@/components/auth/RfidEngineAdminRoute'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (isAuthenticated) return <Navigate to="/scanner" replace />
  return <>{children}</>
}

function AppRoutes() {
  const { isLoading, isAuthenticated, validateSession } = useAuthStore()

  useEffect(() => {
    void validateSession()
  }, [validateSession])

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spin size="large" tip="Validating session…" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="scanner" element={<ScannerConnectPage />} />
        <Route path="scanner/info" element={<ScannerInfoPage />} />
        <Route path="raw-scan" element={<RawScanPage />} />
        <Route path="recent-scans" element={<RecentScansPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="movements/:movementId/documents" element={<DocumentListPage />} />
        <Route path="wizard/capture" element={<WizardCapturePage />} />
        <Route path="wizard/scan" element={<WizardScanPage />} />
        <Route path="wizard/confirm" element={<WizardConfirmPage />} />
        <Route path="wizard/success" element={<WizardSuccessPage />} />
        <Route path="transfer/customer" element={<TransferCustomerPage />} />
        <Route path="transfer/to-customer" element={<TransferToCustomerPage />} />
        <Route path="transfer/docs" element={<TransferDocListPage />} />
        <Route path="transfer/items" element={<TransferItemPickPage />} />
        <Route
          path="engine"
          element={
            <RfidEngineAdminRoute>
              <EngineHubPage />
            </RfidEngineAdminRoute>
          }
        />
        <Route
          path="engine/birth"
          element={
            <RfidEngineAdminRoute>
              <EngineBirthPage />
            </RfidEngineAdminRoute>
          }
        />
        <Route
          path="engine/tag"
          element={
            <RfidEngineAdminRoute>
              <EngineTagPage />
            </RfidEngineAdminRoute>
          }
        />
        <Route
          path="engine/lookup"
          element={
            <RfidEngineAdminRoute>
              <EngineLookupPage />
            </RfidEngineAdminRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return <AppRoutes />
}
